import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageHero } from "./pages";
import { useT } from "./i18n";
import { useStore } from "../store";
import api from "../api";
import { SearchableCountrySelect, PasswordInput } from "./formFields";
import { findCountry } from "./countries";
import {
  Mail, Lock, User, Gift, ArrowRight, CheckCircle2, AlertCircle,
  TrendingUp, ArrowUpDown, CreditCard, Repeat, Send, ShoppingBag,
  BarChart3, Clock, Plus, Minus, Globe, Shield, Briefcase, Users
} from "lucide-react";

/* ═══════════════════════════ LOGIN ═══════════════════════════ */
export function LoginPage() {
  const { t } = useT();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setLoading(true);
    try {
      const r = await api.post("/auth/login", form);
      localStorage.setItem("tgc_jwt", r.data.token);
      useStore.setState({ token: r.data.token, user: r.data.user });
      await useStore.getState().fetchAll();
      nav(window.innerWidth >= 1024 ? "/dashboard" : "/app");
    } catch (e) {
      setErr(e.response?.data?.detail || t("functional.8e3dea"));
    } finally { setLoading(false); }
  };

  return (
    <main data-testid="page-login">
      <section className="tg-page" style={{ padding: "60px 0", maxWidth: 480 }}>
        <div className="tg-card tg-card-glow" style={{ padding: 36 }}>
          <h1 className="tg-h2" style={{ marginBottom: 8 }}>{t("auth.login.title")}</h1>
          <p style={{ color: "var(--tg-text-dim)", marginBottom: 24, fontSize: 14 }}>demo@tgc.app / test1234</p>
          <form onSubmit={submit}>
            <AuthField icon={<Mail size={16} />} type="email" placeholder={t("auth.login.email")}
              value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="login-email" />
            <PasswordInput icon={<Lock size={16} />} placeholder={t("auth.login.password")}
              value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="login-password" />
            {err && <div className="auth-err"><AlertCircle size={14} /> {err}</div>}
            <button className="tg-btn primary" type="submit" style={{ width: "100%", justifyContent: "center" }}
              disabled={loading} data-testid="login-submit">
              {loading ? "..." : t("auth.login.submit")} <ArrowRight size={16} />
            </button>
          </form>
          <p style={{ textAlign: "center", marginTop: 20, color: "var(--tg-text-dim)", fontSize: 14 }}>
            {t("auth.login.noacc")} <Link to="/register" style={{ color: "var(--tg-blue)", fontWeight: 700 }}>{t("auth.login.signup")}</Link>
          </p>
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ REGISTER ═══════════════════════════ */
export function RegisterPage() {
  const { t } = useT();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", referralCode: "", country: "", account_type: "" });
  const [agreed, setAgreed] = useState(false);
  const [err, setErr] = useState(null);
  const [emailErr, setEmailErr] = useState(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [warnNoRef, setWarnNoRef] = useState(false);

  // Read ?ref=CODE from URL and prefill referralCode field (partner referral link)
  // Fallback: check localStorage for a pending ref saved via /r/CODE within last 30 days.
  useEffect(() => {
    const urlRef = searchParams.get("ref");
    if (urlRef) {
      setForm((f) => ({ ...f, referralCode: urlRef.toUpperCase() }));
    } else {
      try {
        const raw = localStorage.getItem("tgc_pending_ref");
        if (raw) {
          const { code, savedAt } = JSON.parse(raw);
          const ageDays = (Date.now() - (savedAt || 0)) / (1000 * 60 * 60 * 24);
          if (code && ageDays < 30) {
            setForm((f) => ({ ...f, referralCode: code.toUpperCase() }));
          }
        }
      } catch (e) { /* ignore */ }
    }
    // STEP 15 — hydrate the tier from a previous selection (persists across reloads)
    try {
      const savedTier = localStorage.getItem("selected_registration_profile");
      if (savedTier && ["PROVIDER", "MERCHANT_RETAIL", "MERCHANT_ENTERPRISE"].includes(savedTier)) {
        setForm((f) => (f.account_type ? f : { ...f, account_type: savedTier }));
      }
    } catch (e) { /* ignore */ }
    // Auto-detect country via geo lookup (non-blocking, soft fallback).
    api.get("/geo/lookup").then((r) => {
      const cc = (r.data?.country || "").toUpperCase();
      if (cc && cc.length === 2) setForm((f) => (f.country ? f : { ...f, country: cc }));
    }).catch(() => { /* fallback: empty — user picks manually */ });
  }, [searchParams]);

  // Live email uniqueness check (debounced)
  useEffect(() => {
    const e = form.email.trim().toLowerCase();
    if (!e || !e.includes("@")) { setEmailErr(null); return; }
    setEmailChecking(true);
    const handle = setTimeout(async () => {
      try {
        const r = await api.get(`/auth/email-exists?email=${encodeURIComponent(e)}`);
        if (r.data?.exists) setEmailErr("Этот Email уже зарегистрирован в системе");
        else setEmailErr(null);
      } catch { /* ignore network issues */ }
      finally { setEmailChecking(false); }
    }, 500);
    return () => { clearTimeout(handle); setEmailChecking(false); };
  }, [form.email]);

  const doRegister = async () => {
    setErr(null); setLoading(true);
    try {
      const r = await api.post("/auth/register", form);
      localStorage.setItem("tgc_jwt", r.data.token);
      try { localStorage.removeItem("tgc_pending_ref"); } catch (e) { /* noop */ }
      useStore.setState({ token: r.data.token, user: r.data.user });
      await useStore.getState().fetchAll();
      nav(window.innerWidth >= 1024 ? "/dashboard" : "/app");
    } catch (e) {
      setErr(e.response?.data?.detail || t("functional.b9f2ca"));
    } finally { setLoading(false); setWarnNoRef(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) { setErr(t("functional.f85e21")); return; }
    if (!form.account_type) { setErr("Выберите категорию регистрации выше"); return; }
    if (!form.firstName.trim()) { setErr("Введите имя"); return; }
    if (!form.lastName.trim())  { setErr("Введите фамилию"); return; }
    if (!form.email.trim())     { setErr("Введите e-mail"); return; }
    if (emailErr)               { setErr(emailErr); return; }
    if (!form.country)          { setErr("Выберите страну проживания"); return; }
    if (form.password.length < 6) { setErr("Пароль минимум 6 символов"); return; }
    // Referral code is MANDATORY. Show the warning modal that explains why
    // a recommender link is required and how to obtain one.
    if (!form.referralCode.trim()) { setWarnNoRef(true); return; }
    await doRegister();
  };

  return (
    <main data-testid="page-register">
      <section className="tg-page" style={{ padding: "60px 0", maxWidth: 820 }}>
        <div className="tg-card tg-card-glow" style={{ padding: 36, background: "linear-gradient(135deg, rgba(255,215,0,0.06), rgba(124,58,237,0.04))" }}>
          <div className="tg-eyebrow gold" style={{ marginBottom: 14 }}><Gift size={12} /> AIRDROP · TGC ≈ €100</div>
          <h1 className="tg-h2" style={{ marginBottom: 12 }}>{t("auth.reg.title")}</h1>

          {/* STEP 15 — Interactive Category Selection Matrix (must be picked BEFORE the form) */}
          <TierSelectionMatrix
            active={form.account_type}
            onPick={(v) => {
              setForm((f) => ({ ...f, account_type: v }));
              try { localStorage.setItem("selected_registration_profile", v); } catch (e) { /* noop */ }
            }}
          />

          <form onSubmit={submit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <AuthField icon={<User size={16} />} placeholder={t("auth.reg.firstname")}
                value={form.firstName} onChange={(v) => setForm({ ...form, firstName: v })} testId="reg-firstname" required />
              <AuthField icon={<User size={16} />} placeholder={t("auth.reg.lastname")}
                value={form.lastName} onChange={(v) => setForm({ ...form, lastName: v })} testId="reg-lastname" required />
            </div>
            <AuthField icon={<Mail size={16} />} type="email" placeholder={t("auth.reg.email")}
              value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="reg-email" required />
            {emailErr && (
              <div data-testid="reg-email-err" style={{ marginTop: -8, marginBottom: 10, color: "#FF3D6C", fontSize: 12, paddingLeft: 6 }}>
                <AlertCircle size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
                {emailErr}
              </div>
            )}
            {emailChecking && !emailErr && (
              <div data-testid="reg-email-checking" style={{ marginTop: -8, marginBottom: 10, color: "var(--tg-text-hint)", fontSize: 11, paddingLeft: 6 }}>
                Проверяем доступность...
              </div>
            )}

            {/* Country select with autocomplete + flag (auto-detected via geo) */}
            <SearchableCountrySelect value={form.country} onChange={(c) => setForm({ ...form, country: c })}
              testId="reg-country" placeholder="Страна проживания *" />

            <PasswordInput icon={<Lock size={16} />} placeholder={t("auth.reg.password")}
              value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="reg-password" required />
            <AuthField icon={<Gift size={16} />} placeholder={t("auth.reg.refcode")}
              value={form.referralCode} onChange={(v) => setForm({ ...form, referralCode: v.toUpperCase() })} testId="reg-refcode" />

            {/* Consent checkbox */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "14px 4px", cursor: "pointer", fontSize: 13, color: "var(--tg-text-dim)", lineHeight: 1.5 }}>
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="reg-consent"
                style={{ width: 18, height: 18, accentColor: "#FFD700", marginTop: 1, flex: "0 0 auto" }} />
              <span>
                {t("reg.agree-with")}{" "}
                <Link to="/legal" target="_blank" style={{ color: "var(--tg-blue)", textDecoration: "underline" }} data-testid="reg-terms-link">{t("functional.ef52dd")}</Link>,{" "}
                <Link to="/legal" target="_blank" style={{ color: "var(--tg-blue)", textDecoration: "underline" }} data-testid="reg-privacy-link">{t("functional.63ad5f")}</Link>{" "}
                {t("reg.and")}{" "}
                <Link to="/legal" target="_blank" style={{ color: "var(--tg-blue)", textDecoration: "underline" }}>{t("functional.8a6088")}</Link>{t("functional.0d9311")}</span>
            </label>

            {err && <div className="auth-err"><AlertCircle size={14} /> {err}</div>}
            <button className="tg-btn gold" type="submit" style={{ width: "100%", justifyContent: "center", marginTop: 4 }}
              disabled={loading || !agreed || !!emailErr} data-testid="reg-submit">
              {loading ? "..." : t("auth.reg.submit")}
            </button>
          </form>
          <p style={{ textAlign: "center", marginTop: 20, color: "var(--tg-text-dim)", fontSize: 14 }}>
            {t("auth.reg.hasacc")} <Link to="/login" style={{ color: "var(--tg-blue)", fontWeight: 700 }}>{t("auth.reg.login")}</Link>
          </p>
        </div>
      </section>

      {/* Warning modal — referral code is MANDATORY. No bypass option. */}
      {warnNoRef && (
        <div onClick={() => setWarnNoRef(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} data-testid="reg-no-ref-modal"
            style={{ maxWidth: 480, background: "linear-gradient(135deg, rgba(255,61,108,0.1), rgba(11,11,16,0.98))", border: "1px solid rgba(255,61,108,0.5)", borderRadius: 16, padding: 28 }}>
            <div style={{ fontSize: 56, textAlign: "center" }}>⚠️</div>
            <h3 style={{ textAlign: "center", margin: "10px 0 14px", color: "#FF3D6C" }}>Регистрация без реферальной ссылки невозможна</h3>
            <p style={{ color: "var(--tg-text)", lineHeight: 1.6, fontSize: 14, margin: "0 0 12px" }}>
              Каждый участник экосистемы TGC попадает в неё <b>только через рекомендателя</b>. Это основа 8-уровневой реферальной программы и распределения AirDrop.
            </p>
            <p style={{ color: "var(--tg-text-dim)", lineHeight: 1.6, fontSize: 13, margin: "0 0 8px" }}>
              <b>Что делать?</b>
            </p>
            <ul style={{ color: "var(--tg-text-dim)", lineHeight: 1.7, fontSize: 13, margin: 0, paddingLeft: 22 }}>
              <li>Свяжитесь с человеком, который рассказал вам о проекте, и попросите его <b>персональную реферальную ссылку</b> (вида <code>/r/CODE</code>)</li>
              <li>Перейдите по ней — код подставится в форму автоматически</li>
              <li>Если у вас нет рекомендателя — задайте вопрос в нашем Telegram-канале</li>
            </ul>
            <div style={{ marginTop: 22 }}>
              <button className="tg-btn gold" onClick={() => setWarnNoRef(false)}
                data-testid="reg-no-ref-back" style={{ width: "100%", justifyContent: "center", fontWeight: 800 }}>
                ↩ Понятно, я введу код пригласителя
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

/* ══ STEP 15 — Interactive Category Selection Matrix ══ */
const TIER_CARDS = [
  {
    key:      "PROVIDER",
    label:    "Network Provider",
    subtitle: "For individual partners, networkers, and validators.",
    fee:      "10 USDT",
    tag:      "Партнёр · Networker",
    color:    "#00F2FF",
    icon:     "🛰️",
    features: ["8-level MLM commission tree", "€100 airdrop → €200 net", "Web3 wallet on Polygon"],
  },
  {
    key:      "MERCHANT_RETAIL",
    label:    "Retail Outlet & Services",
    subtitle: "For shops, cafes, restaurants, and salons.",
    fee:      "50 USDT",
    tag:      "Розница · Салоны · HoReCa",
    color:    "#FFD700",
    icon:     "🏪",
    features: ["POS + 0.4% acquiring fee", "€500 airdrop → €1 000 net", "Smart Tax auto-reserve"],
  },
  {
    key:      "MERCHANT_ENTERPRISE",
    label:    "Enterprise & Factory",
    subtitle: "For industrial complexes, manufacturing, and corporations.",
    fee:      "250 USDT",
    tag:      "Фабрики · Клиники · Корп.",
    color:    "#BC13FE",
    icon:     "🏭",
    features: ["Turbo-Zarplata 10 000+ roster", "€2 500 airdrop → €5 000 net", "B2B Turbo-SWIFT + AI Lawyer"],
  },
];

function TierSelectionMatrix({ active, onPick }) {
  return (
    <div data-testid="tier-selection-matrix" style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: "#8B94B8", textTransform: "uppercase", letterSpacing: 2 }}>
          Шаг 1 · Выберите категорию регистрации
        </div>
        {active && (
          <div style={{ fontSize: 11, color: "#FFD700", display: "flex", alignItems: "center", gap: 4 }} data-testid="tier-selected-label">
            ✓ Выбрано: {TIER_CARDS.find((c) => c.key === active)?.label}
          </div>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {TIER_CARDS.map((c) => {
          const isActive = active === c.key;
          return (
            <button
              type="button" key={c.key} onClick={() => onPick(c.key)}
              data-testid={`reg-tier-${c.key.toLowerCase()}`}
              style={{
                position: "relative", padding: 18, borderRadius: 14, cursor: "pointer", textAlign: "left",
                background: isActive
                  ? `linear-gradient(135deg, ${c.color}25, ${c.color}08 60%, rgba(0,0,0,0.35))`
                  : "linear-gradient(135deg, rgba(255,255,255,0.02), rgba(0,0,0,0.35))",
                border: `1.5px solid ${isActive ? "#FFD700" : "rgba(255,255,255,0.08)"}`,
                boxShadow: isActive
                  ? "0 0 0 3px rgba(255,215,0,0.20), 0 12px 40px rgba(255,215,0,0.30)"
                  : "0 4px 20px rgba(0,0,0,0.25)",
                transition: "all 0.18s ease", color: "#E7ECFF", overflow: "hidden",
                animation: isActive ? "goldPulse 2.2s ease-in-out infinite" : "none",
              }}>
              {isActive && (
                <div data-testid={`reg-tier-active-badge-${c.key.toLowerCase()}`}
                  style={{ position: "absolute", top: 10, right: 10, fontSize: 10, fontWeight: 900,
                           padding: "3px 8px", background: "#FFD700", color: "#050914",
                           borderRadius: 999, letterSpacing: 1 }}>✓ ACTIVE</div>
              )}
              <div style={{ fontSize: 36, marginBottom: 6, filter: isActive ? "none" : "grayscale(30%)" }}>{c.icon}</div>
              <div style={{ fontSize: 11, letterSpacing: 1, color: c.color, textTransform: "uppercase", fontWeight: 800 }}>{c.tag}</div>
              <div style={{ fontSize: 16, fontWeight: 800, marginTop: 4, color: "#E7ECFF" }}>{c.label}</div>
              <div style={{ fontSize: 12, color: "#8B94B8", marginTop: 6, lineHeight: 1.4, minHeight: 32 }}>{c.subtitle}</div>
              <div style={{ marginTop: 10, padding: "6px 10px", borderRadius: 8,
                            background: isActive ? "rgba(255,215,0,0.18)" : "rgba(0,242,255,0.06)",
                            color: isActive ? "#FFD700" : c.color, fontWeight: 900, display: "inline-block" }}>
                Entry: {c.fee}
              </div>
              <ul style={{ marginTop: 12, padding: 0, listStyle: "none", fontSize: 11, color: "#8B94B8" }}>
                {c.features.map((f, i) => (
                  <li key={i} style={{ padding: "2px 0", display: "flex", gap: 6 }}>
                    <span style={{ color: c.color }}>▸</span> {f}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>
      <style>{`
        @keyframes goldPulse {
          0%,100% { box-shadow: 0 0 0 3px rgba(255,215,0,0.20), 0 12px 40px rgba(255,215,0,0.30); }
          50%     { box-shadow: 0 0 0 6px rgba(255,215,0,0.35), 0 12px 60px rgba(255,215,0,0.50); }
        }
      `}</style>
    </div>
  );
}


function AuthField({ icon, type = "text", placeholder, value, onChange, testId, required = false }) {  return (
    <div style={{ position: "relative", marginBottom: 12 }}>
      <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--tg-text-hint)" }}>{icon}</span>
      <input className="auth-input" type={type} placeholder={placeholder} value={value} required={required}
        onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

/* ═══════════════════════════ TAX TRANSPARENCY BANNER ═══════════════════════════ */
export function TaxTransparencyBanner() {
  const { t } = useT();
  return (
    <section className="tg-page tg-section-tight">
      <div className="tg-card" style={{ padding: 18, background: "rgba(16,244,167,0.06)", border: "1px solid rgba(16,244,167,0.3)", display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontSize: 22 }}>🛡️</div>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#10F4A7" }}>{t("functional.1e238d")}</div>
          <div style={{ fontSize: 13, color: "var(--tg-text-dim)", marginTop: 4 }}>{t("functional.853891")}</div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════ GATE (login required) ═══════════════════════════ */
export function GateBanner({ action }) {
  const { t } = useT();
  const actionText = action || t("functional.gate.default-action");
  const { user } = useStore();
  if (user) return null;
  return (
    <section className="tg-page tg-section-tight">
      <div className="tg-card tg-card-glow" style={{ padding: 28, background: "linear-gradient(135deg, rgba(255,215,0,0.12), rgba(0,242,255,0.08))", borderColor: "rgba(255,215,0,0.35)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", justifyContent: "space-between" }}>
          <div>
            <h3 className="tg-h3" style={{ margin: 0 }}>🔐 {t("gate.title")}</h3>
            <p style={{ color: "var(--tg-text-dim)", fontSize: 14, margin: "8px 0 0" }}>{t("gate.msg")}</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Link to="/login" className="tg-btn ghost" data-testid="gate-login">{t("nav.login")}</Link>
            <Link to="/register" className="tg-btn gold" data-testid="gate-register"><Gift size={14} /> {t("nav.register")}</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════ EXCHANGE ═══════════════════════════ */
const COINGECKO_IDS = "bitcoin,ethereum,tether,binancecoin,solana,ripple,cardano,dogecoin";
const PAIR_OPTIONS = [
  { id: "bitcoin",  symbol: "BTC", name: "Bitcoin" },
  { id: "ethereum", symbol: "ETH", name: "Ethereum" },
  { id: "solana",   symbol: "SOL", name: "Solana" },
  { id: "binancecoin", symbol: "BNB", name: "BNB" },
];
function PriceChart({ coinId }) {
  const { t } = useT();
  const [points, setPoints] = React.useState([]);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`https://api.coingecko.com/api/v3/coins/${coinId}/market_chart?vs_currency=usd&days=1`);
        if (!r.ok) throw new Error("rate-limited");
        const d = await r.json();
        if (!cancelled) { setPoints(d.prices || []); setErr(null); }
      } catch (e) { if (!cancelled) setErr(t("functional.8f7bb7")); }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [coinId]);

  if (err) return <div style={{ padding: 40, textAlign: "center", color: "var(--tg-text-hint)" }}>{err}</div>;
  if (!points.length) return <div style={{ padding: 40, textAlign: "center", color: "var(--tg-text-hint)" }}>{t("functional.43742d")}</div>;

  const W = 800, H = 320, P = 20;
  const xs = points.map(([ts]) => ts);
  const ys = points.map(([_, p]) => p);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const yMin = Math.min(...ys), yMax = Math.max(...ys);
  const sx = (x) => P + (W - 2 * P) * (x - xMin) / (xMax - xMin);
  const sy = (y) => H - P - (H - 2 * P) * (y - yMin) / (yMax - yMin);
  const path = points.map(([ts, p], i) => `${i === 0 ? "M" : "L"} ${sx(ts).toFixed(1)} ${sy(p).toFixed(1)}`).join(" ");
  const areaPath = `${path} L ${sx(xMax).toFixed(1)} ${H - P} L ${sx(xMin).toFixed(1)} ${H - P} Z`;
  const last = ys[ys.length - 1], first = ys[0];
  const up = last >= first;
  const stroke = up ? "#10F4A7" : "#FF3D6C";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 320, display: "block" }}>
      <defs>
        <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.3" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#grad)" />
      <path d={path} fill="none" stroke={stroke} strokeWidth="2" />
      {/* gridlines */}
      {[0.25, 0.5, 0.75].map((f, i) => (
        <line key={i} x1={P} y1={P + (H - 2 * P) * f} x2={W - P} y2={P + (H - 2 * P) * f}
          stroke="rgba(255,255,255,0.04)" strokeDasharray="3 4" />
      ))}
      {/* labels */}
      <text x={W - P - 4} y={P + 12} fontSize="11" fill="var(--tg-text-hint)" textAnchor="end">${yMax.toLocaleString("en-US", { maximumFractionDigits: 2 })}</text>
      <text x={W - P - 4} y={H - P - 4} fontSize="11" fill="var(--tg-text-hint)" textAnchor="end">${yMin.toLocaleString("en-US", { maximumFractionDigits: 2 })}</text>
    </svg>
  );
}

export function ExchangePage() {
  const { t } = useT();
  const [coins, setCoins] = React.useState([]);
  const [pairId, setPairId] = React.useState("bitcoin");
  const [tgcPrice] = React.useState(0.10);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${COINGECKO_IDS}&order=market_cap_desc&sparkline=false&price_change_percentage=24h`);
        if (!r.ok) throw new Error("rate-limited");
        const data = await r.json();
        setCoins(data); setErr(null);
      } catch (e) { setErr(t("functional.c4254f")); }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);
  const tgcChange = (Math.sin(Date.now() / 1e7) * 5).toFixed(2);
  const currentPair = coins.find((c) => c.id === pairId);

  return (
    <main data-testid="page-exchange">
      <PageHero eyebrow="Turbo Exchange" title={<>{t("functional.12ae8c")}<span className="accent">{t("functional.6187c9")}</span></>}
        lead={t("functional.31c376")} />
      <GateBanner action={t("functional.gate.exchange")} />
      <TaxTransparencyBanner />

      {/* TICKER GRID */}
      <section className="tg-page tg-section-tight">
        {err && <div className="auth-err"><AlertCircle size={14} /> {err} {t("functional.0d4c45")}</div>}
        <div className="tg-bento">
          <div className="tg-card wide" style={{ borderColor: "rgba(255,215,0,0.4)", background: "linear-gradient(135deg, rgba(255,215,0,0.08), transparent 60%)" }} data-testid="ticker-tgc">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 18, background: "linear-gradient(135deg, #FFD700, #00F2FF)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, color: "#000" }}>T</div>
              <div>
                <div style={{ fontWeight: 700 }}>TGC <span style={{ color: "var(--tg-text-hint)", fontWeight: 400 }}>Turbo Global</span></div>
                <div className="tg-stat-label">Native token</div>
              </div>
            </div>
            <div className="tg-stat-num blue" style={{ marginTop: 14, fontSize: 28 }}>${tgcPrice.toFixed(4)}</div>
            <div style={{ fontSize: 13, color: parseFloat(tgcChange) >= 0 ? "#10F4A7" : "#FF3D6C", fontWeight: 700 }}>
              {parseFloat(tgcChange) >= 0 ? "+" : ""}{tgcChange}% (24h)
            </div>
          </div>
          {coins.map((c) => (
            <div key={c.id} className="tg-card wide" data-testid={`ticker-${c.symbol}`}
              onClick={() => PAIR_OPTIONS.find((p) => p.id === c.id) && setPairId(c.id)}
              style={{ cursor: PAIR_OPTIONS.find((p) => p.id === c.id) ? "pointer" : "default", borderColor: c.id === pairId ? "var(--tg-blue)" : undefined }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <img src={c.image} alt={c.name} style={{ width: 36, height: 36, borderRadius: 18 }} />
                <div>
                  <div style={{ fontWeight: 700 }}>{c.symbol.toUpperCase()} <span style={{ color: "var(--tg-text-hint)", fontWeight: 400 }}>{c.name}</span></div>
                  <div className="tg-stat-label">Cap ${(c.market_cap / 1e9).toFixed(2)}B</div>
                </div>
              </div>
              <div className="tg-stat-num" style={{ marginTop: 14, fontSize: 26, color: "var(--tg-blue)" }}>
                ${c.current_price.toLocaleString("en-US", { maximumFractionDigits: c.current_price < 1 ? 4 : 2 })}
              </div>
              <div style={{ fontSize: 13, color: c.price_change_percentage_24h >= 0 ? "#10F4A7" : "#FF3D6C", fontWeight: 700 }}>
                {c.price_change_percentage_24h >= 0 ? "+" : ""}{c.price_change_percentage_24h?.toFixed(2)}% (24h)
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="tg-page tg-section-tight">
        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 20 }}>
          <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: 16, borderBottom: "1px solid var(--tg-border)", display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
              <select className="auth-input" style={{ width: "auto", padding: "8px 30px 8px 12px", flex: "0 0 auto" }}
                value={pairId} onChange={(e) => setPairId(e.target.value)} data-testid="pair-select">
                {PAIR_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.symbol}/USDT — {p.name}</option>)}
              </select>
              {currentPair && (
                <>
                  <div>
                    <div className="tg-stat-label">{t("functional.682fa8")}</div>
                    <div className="tg-stat-num blue" style={{ fontSize: 22 }}>${currentPair.current_price.toLocaleString("en-US", { maximumFractionDigits: 2 })}</div>
                  </div>
                  <div>
                    <div className="tg-stat-label">24h</div>
                    <div style={{ color: currentPair.price_change_percentage_24h >= 0 ? "#10F4A7" : "#FF3D6C", fontWeight: 700, fontSize: 16 }}>
                      {currentPair.price_change_percentage_24h >= 0 ? "+" : ""}{currentPair.price_change_percentage_24h?.toFixed(2)}%
                    </div>
                  </div>
                  <div>
                    <div className="tg-stat-label">Volume 24h</div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>${(currentPair.total_volume / 1e9).toFixed(2)}B</div>
                  </div>
                </>
              )}
              <div style={{ marginLeft: "auto" }}>
                <div className="tg-stat-label">{t("functional.7a1120")}</div>
                <div style={{ fontWeight: 700, fontSize: 12, color: "#10F4A7" }}>● CoinGecko · live (60s)</div>
              </div>
            </div>
            <PriceChart coinId={pairId} />
          </div>
          <div className="tg-card" data-testid="exchange-order-panel">
            <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
              <button className="tg-btn primary" style={{ flex: 1, justifyContent: "center", padding: "10px" }}>{t("functional.d03830")}</button>
              <button className="tg-btn ghost" style={{ flex: 1, justifyContent: "center", padding: "10px" }}>{t("functional.23c51e")}</button>
            </div>
            <label className="tg-stat-label" style={{ margin: 0 }}>{t("functional.2f987a")}</label>
            <input className="auth-input" placeholder="1000" />
            <label className="tg-stat-label" style={{ margin: "10px 0 0" }}>{t("functional.df0d3b")}</label>
            <input className="auth-input" value="102.50" readOnly />
            <Link to="/register" className="tg-btn gold" style={{ width: "100%", justifyContent: "center", marginTop: 14 }}
              data-testid="exchange-trade-btn"><TrendingUp size={14} />{t("functional.5df97b")}</Link>
          </div>
        </div>
      </section>
      {/* Order book */}
      <section className="tg-page tg-section">
        <h2 className="tg-h2">{t("functional.6fb550")}</h2>
        <div className="tg-card" style={{ padding: 0 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid var(--tg-border)" }}>
            <div style={{ padding: "10px 16px", fontSize: 11, color: "#10F4A7", fontWeight: 700, textTransform: "uppercase" }}>BIDS</div>
            <div style={{ padding: "10px 16px", fontSize: 11, color: "#FF3D6C", fontWeight: 700, textTransform: "uppercase", textAlign: "right" }}>ASKS</div>
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px dashed var(--tg-border)", fontSize: 13, fontFeatureSettings: '"tnum"' }}>
              <div style={{ padding: "8px 16px", color: "#10F4A7" }}>${(0.1024 - i * 0.0002).toFixed(4)} <span style={{ color: "var(--tg-text-dim)", marginLeft: 8 }}>× {(12000 + i * 840).toLocaleString("ru-RU")}</span></div>
              <div style={{ padding: "8px 16px", color: "#FF3D6C", textAlign: "right" }}>${(0.1026 + i * 0.0002).toFixed(4)} <span style={{ color: "var(--tg-text-dim)", marginLeft: 8 }}>× {(8400 + i * 320).toLocaleString("ru-RU")}</span></div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ SWIFT ═══════════════════════════ */
export function SwiftPage() {
  const { t } = useT();
  return (
    <main data-testid="page-swift">
      <PageHero eyebrow={t("header.products.swift.title")} title={<>{t("functional.1a09e2")}<span className="accent">{t("functional.5d954c")}</span></>}
        lead={t("functional.6de13a")} />
      <GateBanner action={t("functional.gate.swift")} />
      <TaxTransparencyBanner />
      <section className="tg-page tg-section-tight">
        <div className="tg-card" style={{ padding: 32, maxWidth: 640, margin: "0 auto" }}>
          <h3 className="tg-h3">{t("functional.964c55")}</h3>
          <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
            <AuthField icon={<User size={16} />} placeholder={t("functional.191088")} value="" onChange={() => {}} />
            <AuthField icon={<Briefcase size={16} />} placeholder="IBAN / Account number" value="" onChange={() => {}} />
            <AuthField icon={<Briefcase size={16} />} placeholder="SWIFT/BIC" value="" onChange={() => {}} />
            <AuthField icon={<Globe size={16} />} placeholder={t("functional.85656b")} value="" onChange={() => {}} />
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
              <AuthField icon={<TrendingUp size={16} />} placeholder={t("pages3.cf59eb")} value="" onChange={() => {}} />
              <select className="auth-input"><option>EUR</option><option>USD</option><option>GBP</option></select>
            </div>
            <div className="tg-card" style={{ background: "rgba(0,242,255,0.06)", padding: 14, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span>{t("functional.c5224e")}</span><b>— TGC</b></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}><span>{t("functional.4b8eb9")}</span><b>— TGC</b></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}><span>{t("functional.05517e")}</span><b style={{ color: "#10F4A7" }}>{t("functional.50621a")}</b></div>
            </div>
            <Link to="/register" className="tg-btn primary" style={{ justifyContent: "center", padding: 14 }}
              data-testid="swift-send-btn"><Send size={16} />{t("functional.467aca")}</Link>
          </div>
        </div>
      </section>
      <section className="tg-page tg-section">
        <div className="tg-bento">
          {[
            ["🏦", t("functional.f2f641"), t("functional.e4ff0d")],
            ["⚡", t("functional.3a7086"), t("functional.bec011")],
            ["💎", "0.1-0.5%", t("functional.51b01a")],
            ["🛡️", "Compliance", t("functional.a2e8cd")],
          ].map(([ic, t, d], i) => (
            <div key={i} className="tg-card" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>{ic}</div>
              <h3 className="tg-h3" style={{ fontSize: 16 }}>{t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 13 }}>{d}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ P2P EXCHANGE ═══════════════════════════ */
export function P2PPage() {
  const { t } = useT();
  const offers = [
    { u: t("pages.e86db5"), r: 4.9, d: 148, p: 0.098, lim: "€100–5000", pay: ["Kaspi", "Card"],  avatar: "🇰🇿" },
    { u: "Marcus T.",r: 4.8, d: 89,  p: 0.099, lim: "€500–10000", pay: ["Revolut", "Wise"], avatar: "🇦🇪" },
    { u: t("functional.06eea1"),r: 5.0, d: 203, p: 0.100, lim: "€50–2000", pay: ["Humo", "Uzcard"], avatar: "🇺🇿" },
    { u: t("pages.637509"),r: 4.7, d: 52,  p: 0.101, lim: "€1000–20k", pay: ["SWIFT", "USDT"], avatar: "🇰🇿" },
    { u: "Aisha H.",r: 4.9, d: 112, p: 0.102, lim: "€100–3000", pay: ["GCash", "Maya"], avatar: "🇵🇭" },
  ];
  return (
    <main data-testid="page-p2p">
      <PageHero eyebrow="P2P Exchange" title={<>{t("functional.2fe095")}<span className="accent">{t("functional.409094")}</span></>}
        lead={t("functional.24cf0d")} />
      <GateBanner action={t("functional.gate.p2p")} />
      <TaxTransparencyBanner />
      <section className="tg-page tg-section-tight">
        <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: 16, borderBottom: "1px solid var(--tg-border)", display: "flex", gap: 12, flexWrap: "wrap" }}>
            <select className="auth-input" style={{ flex: "0 0 140px" }}>
              <option>{t("functional.3e367f")}</option><option>{t("functional.e64582")}</option>
            </select>
            <select className="auth-input" style={{ flex: "0 0 140px" }}>
              <option>EUR</option><option>USD</option><option>RUB</option><option>KZT</option><option>UZS</option>
            </select>
            <select className="auth-input" style={{ flex: "0 0 180px" }}>
              <option>{t("functional.a2ffcb")}</option><option>Kaspi</option><option>SWIFT</option><option>Revolut</option>
            </select>
          </div>
          <table className="tg-table">
            <thead><tr><th>{t("functional.3e3eba")}</th><th>{t("functional.682fa8")}</th><th>{t("functional.35536d")}</th><th>{t("functional.e3c1f3")}</th><th></th></tr></thead>
            <tbody>
              {offers.map((o, i) => (
                <tr key={i}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 22 }}>{o.avatar}</span>
                      <div>
                        <div style={{ fontWeight: 700 }}>{o.u}</div>
                        <div className="tg-stat-label" style={{ margin: 0 }}>⭐ {o.r} · {o.d} {t("functional.ed7917")}</div>
                      </div>
                    </div>
                  </td>
                  <td><b style={{ color: "#10F4A7" }}>${o.p}</b></td>
                  <td>{o.lim}</td>
                  <td style={{ display: "flex", gap: 4, flexWrap: "wrap", padding: "10px 16px" }}>
                    {o.pay.map((p) => <span key={p} style={{ padding: "3px 8px", fontSize: 11, background: "rgba(0,242,255,0.08)", color: "#00F2FF", borderRadius: 6, border: "1px solid rgba(0,242,255,0.3)" }}>{p}</span>)}
                  </td>
                  <td><Link to="/register" className="tg-btn primary" style={{ padding: "8px 16px", fontSize: 13 }} data-testid={`p2p-buy-${i}`}>{t("functional.d03830")}</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ ACQUIRING (MERCHANT SIGNUP) ═══════════════════════════ */
export function AcquiringPage() {
  const { t } = useT();
  const [form, setForm] = useState({ company: "", inn: "", email: "", phone: "", type: "cafe", volume: "100k" });
  const [sent, setSent] = useState(false);
  return (
    <main data-testid="page-acquiring">
      <PageHero eyebrow="Turbo Acquiring" title={<>{t("functional.6e8fc4")}<span className="accent">{t("functional.0c9b74")}</span></>}
        lead={t("functional.5db217")} />
      <TaxTransparencyBanner />

      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {[
            ["💳", t("functional.616000"), t("functional.94d0c9")],
            ["⚡", t("functional.55f0e0"), "REST + WebSocket. SDK PHP/Node/Python."],
            ["📊", "Dashboard", t("functional.6e374e")],
            ["🏦", t("functional.68f5af"), t("functional.c7e558")],
          ].map(([ic, t, d], i) => (
            <div key={i} className="tg-card">
              <div style={{ fontSize: 30, marginBottom: 8 }}>{ic}</div>
              <h3 className="tg-h3" style={{ fontSize: 16 }}>{t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 13 }}>{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="tg-page tg-section">
        <div className="tg-card" style={{ padding: 36, maxWidth: 640, margin: "0 auto" }}>
          <h2 className="tg-h3">{t("functional.ea892b")}</h2>
          <p style={{ color: "var(--tg-text-dim)", fontSize: 14, marginBottom: 20 }}>{t("functional.e58fa1")}</p>
          {sent ? (
            <div style={{ padding: 32, textAlign: "center", background: "rgba(16,244,167,0.08)", borderRadius: 16, border: "1px solid rgba(16,244,167,0.3)" }}>
              <CheckCircle2 size={48} color="#10F4A7" />
              <h3 className="tg-h3" style={{ marginTop: 12 }}>{t("functional.3d478c")}</h3>
              <p style={{ color: "var(--tg-text-dim)" }}>{t("functional.6b7c9f")} {form.email} {t("functional.69e6d7")}</p>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); setSent(true); }}>
              <AuthField icon={<Briefcase size={16} />} placeholder={t("functional.7b39c2")}
                value={form.company} onChange={(v) => setForm({ ...form, company: v })} testId="acq-company" />
              <AuthField icon={<Briefcase size={16} />} placeholder={t("functional.149a54")}
                value={form.inn} onChange={(v) => setForm({ ...form, inn: v })} testId="acq-inn" />
              <AuthField icon={<Mail size={16} />} type="email" placeholder={t("functional.c86615")}
                value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="acq-email" />
              <AuthField icon={<User size={16} />} placeholder={t("functional.2928e1")}
                value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="acq-phone" />
              <select className="auth-input" style={{ marginBottom: 12 }} value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="acq-type">
                <option value="cafe">{t("functional.2045dc")}</option>
                <option value="shop">{t("functional.22df6e")}</option>
                <option value="service">{t("functional.4e1a0e")}</option>
                <option value="travel">{t("functional.e33037")}</option>
                <option value="other">{t("functional.ff85ae")}</option>
              </select>
              <select className="auth-input" style={{ marginBottom: 12 }} value={form.volume}
                onChange={(e) => setForm({ ...form, volume: e.target.value })} data-testid="acq-volume">
                <option value="10k">{t("functional.9b6c58")}</option>
                <option value="100k">{t("functional.b6f728")}</option>
                <option value="1m">{t("functional.4f880e")}</option>
                <option value="1m+">{t("functional.9d72e3")}</option>
              </select>
              <button className="tg-btn primary" type="submit" style={{ width: "100%", justifyContent: "center" }}
                data-testid="acq-submit">
                <Send size={14} />{t("functional.7094b4")}</button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ FAQ FOR USERS (re-export with new content) ═══════════════════════════ */
const USER_QUESTION_KEYS = [
  ["functional.q1.q", "functional.q1.a"],
  ["functional.q2.q", "functional.q2.a"],
  ["functional.q3.q", "functional.q3.a"],
  ["functional.q4.q", "functional.q4.a"],
  ["functional.q5.q", "functional.q5.a"],
  ["functional.q6.q", "functional.q6.a"],
  ["functional.q7.q", "functional.q7.a"],
  ["functional.q8.q", "functional.q8.a"],
  ["functional.q9.q", "functional.q9.a"],
  ["functional.q10.q", "functional.q10.a"],
  ["functional.q11.q", "functional.q11.a"],
  ["functional.q12.q", "functional.q12.a"],
];
export function FAQParticipantsPage() {
  const { t } = useT();
  const [open, setOpen] = useState(0);
  const USER_QUESTIONS = USER_QUESTION_KEYS.map(([qk, ak]) => [t(qk), t(ak)]);
  return (
    <main data-testid="page-faq">
      <PageHero eyebrow="FAQ" title={<>{t("functional.6e2cdf")}<span className="accent">{t("functional.5d6bd9")}</span></>} />
      <section className="tg-page tg-section-tight">
        <div className="tg-card">
          {USER_QUESTIONS.map(([q, a], i) => (
            <div key={i} className="tg-faq-item">
              <div className="tg-faq-q" onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-q-${i}`}>
                <span>{q}</span>
                {open === i ? <Minus size={20} /> : <Plus size={20} />}
              </div>
              {open === i && <div className="tg-faq-a">{a}</div>}
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
